// A lesson: one line of a course, the way an opening is taught — the moves
// in, then one reply and your line against it. Watching, the line is walked
// back and on a move at a time, each move with why, the moments that make
// the opening — a key move, a sacrifice, the only move — marked. Trying,
// every move is from memory. A mistake's punishment is found, never shown.
// Either way is a press away from the other. Then the drill keeps it. What each lesson is and how
// far you are is lesson-view.ts; the lessons are the server's (OPENINGS.md
// §5.5).

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Board } from "../board/Board.tsx";
import { errorText, isSignedOut, reportFault, sendJson } from "../app/api.ts";
import { persist, Superseded, type NetState } from "../app/net.ts";
import { scoreOf } from "../app/engine.ts";
import { cap } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { prefs } from "../app/prefs.ts";
import { follow, goBack, navigate } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { PuzzleBoard, ScoreStrip, usePuzzle } from "../board/PuzzleBoard.tsx";
import { formatScore, winChances, type Score } from "../board/scores.ts";
import type { MoveInfo } from "../board/puzzle.ts";
import { BoardMenu, BoardPlaceholder, BoardScreen } from "./BoardScreen.tsx";
import { LineMoves, LineRow } from "./LineRow.tsx";
import { wins } from "./Drill.tsx";
import { humanTag } from "../app/kinds.ts";
import {
  afterText, askText, boardLine, describe, drillFrom, ideas, momentName, pathText, said, subtitleOf, titleOf, watchAt, watchedAsLine,
  type Lesson, type LessonStep, type Lessons, type Mode,
} from "./lesson-view.ts";

interface Data {
  repertoire: { id: number; name: string; color: "white" | "black" };
  lessons: Lessons | null;
}

export function LessonScreen({ params, query }: ScreenProps) {
  const id = params.id!;
  const n = Number(params.n) || 0;
  const mode: Mode = query.get("mode") === "try" ? "try" : "watch";
  const back = `/openings/${id}`;
  const meta = { title: "Lesson", nav: "openings", back, backLabel: "Repertoire" };
  const [data, setData] = useState<{ kind: "loading" } | { kind: "error"; text: string } | { kind: "ok"; data: Data }>({ kind: "loading" });
  const [net, setNet] = useState<NetState>({ kind: "ok" });
  const [attempt, setAttempt] = useState(0);
  const asking = useRef(0);

  const load = useCallback(async () => {
    const mine = ++asking.current;
    try {
      const got = await persist((signal) => sendJson<Data>("GET", `/api/repertoires/${id}/lessons`, undefined, { signal }), {
        onState: (state) => mine === asking.current && setNet(state),
        superseded: () => mine !== asking.current,
      });
      setNet({ kind: "ok" });
      setData({ kind: "ok", data: got });
    } catch (err) {
      if (isSignedOut(err) || err instanceof Superseded) return;
      setNet({ kind: "ok" });
      setData({ kind: "error", text: errorText(err) });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (data.kind === "ok") {
    const list = data.data.lessons?.list || [];
    const lesson = list[n];
    if (!lesson || !boardLine(lesson)) {
      return (
        <div className="screen">
          <BackButton meta={meta} />
          <div className="notice">
            {list.length ? `There is no lesson ${n + 1}: this course has ${list.length}.` : "This repertoire has no lessons: its line does not branch yet."}{" "}
            <a {...follow(back)}>Back to the repertoire</a>
          </div>
        </div>
      );
    }
    const View = watchedAsLine(lesson, mode) ? WatchView : LessonView;
    return (
      <div className="screen lesson-screen">
        <BackButton meta={meta} />
        <View
          key={`${n}:${mode}:${attempt}`}
          data={data.data}
          n={n}
          mode={mode}
          back={back}
          onAgain={() => setAttempt((a) => a + 1)}
          onDone={(stage) => {
            // What the page knows follows what was saved, so the buttons
            // offered next are right without asking again.
            setData((was) => {
              if (was.kind !== "ok" || !was.data.lessons) return was;
              const next = was.data.lessons.list.map((l, i) => (i === n ? { ...l, [stage]: true } : l));
              return { kind: "ok", data: { ...was.data, lessons: { ...was.data.lessons, list: next } } };
            });
          }}
        />
      </div>
    );
  }
  return (
    <div className="screen lesson-screen">
      <BackButton meta={meta} />
      {data.kind === "error" ? (
        <div className="notice error">{data.text}</div>
      ) : (
        <BoardPlaceholder
          status={net.kind === "offline" ? "No connection — trying again; the lesson comes as soon as it is back." : net.kind === "slow" ? "Connecting…" : "Opening the lesson…"}
          lead={
            <button type="button" className="btn menu-btn" onClick={() => goBack(back)}>
              <Icon name="back" />
              Back
            </button>
          }
          slots={
            <button type="button" className="btn advance" disabled>
              Next lesson
              <Icon name="next" />
            </button>
          }
        />
      )}
    </div>
  );
}

interface ViewProps {
  data: Data;
  n: number;
  mode: Mode;
  back: string;
  onAgain: () => void;
  onDone: (stage: "watched" | "tried") => void;
}

/** Why a move is played: its note, else what it plainly does. */
function whyOf(lessons: Lessons, lesson: Lesson, step: LessonStep): string | null {
  const all = lesson.steps;
  const i = all.indexOf(step);
  const previous = i > 0 ? all[i - 1]!.uci : lesson.kind === "opening" ? undefined : lessons.way_in[lessons.way_in.length - 1]?.uci;
  return step.note || describe(step.fen, step.uci, previous);
}

/** Their move, and why. */
function Theirs({ lessons, lesson, step, next }: { lessons: Lessons; lesson: Lesson; step: LessonStep; next?: boolean }) {
  const why = whyOf(lessons, lesson, step);
  return (
    <>
      <div className="drill-note-title">
        {lesson.kind === "mistake" && step === lesson.steps[0] ? <span className="badge trap">Mistake</span> : null} {next ? "They reply" : "They play"} <b>{said(step)}</b>.
      </div>
      {why ? <p>{why}</p> : null}
    </>
  );
}

/** The way a lesson is played, Watch or From memory: either a press from
 *  the other at any move. First in the sheet, so that nothing said about a
 *  move ever pushes it out of reach. */
function LessonModes({ data, n, mode }: { data: Data; n: number; mode: Mode }) {
  const to = (m: Mode) => {
    if (m !== mode) navigate(`/openings/${data.repertoire.id}/lesson/${n}${m === "try" ? "?mode=try" : ""}`);
  };
  return (
    <div className="lesson-modes" role="group" aria-label="How the lesson is played">
      <button type="button" className="lesson-mode" aria-pressed={mode === "watch"} title="Every move shown, back and on" onClick={() => to("watch")}>
        <Icon name="eye" />
        Watch
      </button>
      <button type="button" className="lesson-mode" aria-pressed={mode === "try"} title="Every move yours to find" onClick={() => to("try")}>
        <Icon name="play" />
        From memory
      </button>
    </div>
  );
}

/** The lesson's name and where it stands. */
function LessonHead({ data, n }: { data: Data; n: number }) {
  const rep = data.repertoire;
  const list = data.lessons!.list;
  const lesson = list[n]!;
  return (
    <>
      <div className="puzzle-badges">
        <span className="badge opening">
          Lesson {n + 1} of {list.length}
        </span>
        <a {...follow(`/openings/${rep.id}`)}>{rep.name}</a>
      </div>
      <h2 className="puzzle-title">{titleOf(lesson, rep.name)}</h2>
      <p className="faint lesson-sub">
        {subtitleOf(lesson)} · {cap(rep.color)}
      </p>
    </>
  );
}

/** What a lesson leaves you with, once it is over, and what to do next. */
function LessonDone({ data, n, mode, moves, missed, onAgain }: { data: Data; n: number; mode: Mode; moves: number; missed: number; onAgain: () => void }) {
  const rep = data.repertoire;
  const list = data.lessons!.list;
  const lesson = list[n]!;
  const hasNext = n + 1 < list.length;
  const from = drillFrom(lesson);
  const taken = ideas(lesson);
  return (
    <div className="drill-done lesson-done">
      <b>Lesson done</b> · {moves} {moves === 1 ? "move" : "moves"}
      {mode === "try" ? ` · ${missed} missed` : ""}
      {lesson.punish ? (
        <p className="lesson-then">
          {wins(lesson.punish)}
          {lesson.punish.motifs.filter((m) => m !== "mate").slice(0, 2).map((m) => (
            <span key={m} className="tag">
              {" "}
              {humanTag(m)}
            </span>
          ))}
        </p>
      ) : null}
      {taken.length ? (
        <ul className="lesson-ideas">
          {taken.map((idea) => (
            <li key={idea.label}>
              <b>{idea.label}</b> <span className="badge lesson-moment">{momentName(idea.moment)}</span>
              {idea.note ? <span> {idea.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="lesson-then">
        {mode === "watch"
          ? "Now play it from memory: every move yours to find."
          : missed
            ? "Once more without a miss, or drill it: the drill brings each move back until it stays."
            : "Drill it: the drill brings each move back until it stays."}
      </p>
      <div className="drill-done-actions">
        {mode === "watch" ? (
          <>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/openings/${rep.id}/lesson/${n}?mode=try`)}>
              <Icon name="play" />
              Try it from memory
            </button>
            <button type="button" className="btn btn-sm" onClick={onAgain}>
              <Icon name="refresh" />
              Watch again
            </button>
          </>
        ) : (
          <>
            {from ? (
              <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/openings/${rep.id}/drill?from=${encodeURIComponent(from)}`)}>
                <Icon name="target" />
                Drill it
              </button>
            ) : null}
            <button type="button" className="btn btn-sm" onClick={onAgain}>
              <Icon name="refresh" />
              Try again
            </button>
            <button type="button" className="btn btn-sm" onClick={() => navigate(`/openings/${rep.id}/lesson/${n}`)}>
              <Icon name="eye" />
              Watch again
            </button>
          </>
        )}
        {hasNext ? (
          <button type="button" className="btn btn-sm" onClick={() => navigate(`/openings/${rep.id}/lesson/${n + 1}`)}>
            <Icon name="next" />
            Next lesson
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** The bar's last slot: once watched, trying it; otherwise the next lesson. */
function Advance({ data, n, mode, done, back }: { data: Data; n: number; mode: Mode; done: boolean; back: string }) {
  const rep = data.repertoire;
  const hasNext = n + 1 < data.lessons!.list.length;
  if (done && mode === "watch")
    return (
      <button type="button" className="btn advance btn-primary" title="Play the lesson again, every move from memory" onClick={() => navigate(`/openings/${rep.id}/lesson/${n}?mode=try`)}>
        Try it
        <Icon name="next" />
      </button>
    );
  return (
    <button
      type="button"
      className={"btn advance" + (done ? " btn-primary" : "")}
      onClick={() => (hasNext ? navigate(`/openings/${rep.id}/lesson/${n + 1}`) : goBack(back))}
      title={hasNext ? "The next lesson of this course" : "Back to the course: this is its last lesson"}
    >
      {hasNext ? "Next lesson" : "All lessons"}
      <Icon name="next" />
    </button>
  );
}

/** Saves how far a lesson got, once. */
function saveStage(rep: Data["repertoire"], lesson: Lesson, stage: "watched" | "tried") {
  persist((signal) => sendJson("POST", `/api/repertoires/${rep.id}/lessons`, { lesson: lesson.key, stage }, { signal })).catch((err) => {
    if (!isSignedOut(err)) toast(`Could not save the lesson: ${errorText(err)}`);
  });
}

/**
 * Watching: the line walked a move at a time. The arrow is the move about
 * to be played, green for yours and red for theirs, and the sheet says why;
 * › plays it — so does the move on the board, or a tap on the arrow — and ‹
 * takes it back. Reaching the end is having watched it.
 */
function WatchView({ data, n, mode, back, onAgain, onDone }: ViewProps) {
  const rep = data.repertoire;
  const lessons = data.lessons!;
  const lesson = lessons.list[n]!;
  const line = boardLine(lesson)!;
  const steps = line.steps;
  const [at, setAt] = useState(0);
  const [watched, setWatched] = useState(false);
  const [scores, setScores] = useState<Record<string, Score | null>>({});
  const w = watchAt(steps, at);
  const ended = !w.next;

  const go = (to: number) => setAt(watchAt(steps, to).at);
  useEffect(() => {
    if (!ended || watched) return;
    setWatched(true);
    onDone("watched");
    saveStage(rep, lesson, "watched");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended]);
  useEffect(() => {
    if (w.fen in scores) return;
    let live = true;
    void scoreOf(w.fen).then((sc) => live && setScores((was) => ({ ...was, [w.fen]: sc })));
    return () => {
      live = false;
    };
  }, [w.fen, scores]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && e.target.closest("input, textarea, select")) return;
      if (e.key === "ArrowLeft") setAt((a) => watchAt(steps, a - 1).at);
      else if (e.key === "ArrowRight") setAt((a) => watchAt(steps, a + 1).at);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps]);

  const known = scores[w.fen];
  const score = known ? { text: formatScore(known), whiteShare: Math.round((winChances(known) + 1) * 50) } : { text: w.fen in scores ? "–" : "…", whiteShare: 50 };
  const next = w.next;
  const why = next ? whyOf(lessons, lesson, next) : null;
  const after = next?.moment ? afterText(next.moment) : "";
  const lead = line.lead[line.lead.length - 1];
  const stepBtn = (which: "back" | "on", title: string, disabled: boolean, onClick: () => void) => (
    <button type="button" className={"mv-chip step step-" + which} title={title} aria-label={title} disabled={disabled} onClick={onClick}>
      <Icon name={which === "back" ? "stepBack" : "stepOn"} />
    </button>
  );
  const name = titleOf(lesson, rep.name);

  return (
    <BoardScreen
      className={"lesson-line lesson-watch" + (ended ? " solved" : "")}
      board={
        <div className="board-zone">
          <Board
            className="view-board"
            fen={w.fen}
            orientation={rep.color}
            last={w.played?.uci ?? null}
            replies={next ? [{ uci: next.uci, label: next.san, tone: next.yours ? "line" : "reply" }] : []}
            tone={next?.yours ? "line" : "reply"}
            onReply={() => go(at + 1)}
            input={next ? { legal: [next.uci], onMove: () => go(at + 1) } : null}
          />
          <ScoreStrip score={score} />
        </div>
      }
      line={
        <div className="rep-caption">
          <div className="rep-caption-head">
            {stepBtn("back", at > 0 ? "One move back" : "This is the start of the lesson", !w.canBack, () => go(at - 1))}
            <span className="rep-caption-mid">
              <span className="rep-caption-move">
                <b>{w.played ? said(w.played) : lead ? said(lead) : "Start"}</b>
                {w.played?.moment ? <span className="move-mark lesson-mark">{momentName(w.played.moment).toLowerCase()}</span> : null}
              </span>
              <span className="faint lesson-count num">
                {at} / {steps.length}
              </span>
            </span>
            {stepBtn("on", next ? `On: ${said(next)}` : "The lesson ends here", !w.canOn, () => go(at + 1))}
          </div>
        </div>
      }
      bar={
        <>
          <button type="button" className="btn menu-btn" onClick={() => goBack(back)}>
            <Icon name="back" />
            Back
          </button>
          {/* The hint's slot, kept: watching, every move is shown already. */}
          <button type="button" className="btn hint-btn spent" disabled aria-hidden="true" tabIndex={-1}>
            <Icon name="eye" />
            Hint
          </button>
          <Advance data={data} n={n} mode={mode} done={watched} back={back} />
          <BoardMenu
            shown={() => ({
              fen: w.fen,
              start: line.fen,
              moves: steps.slice(0, w.at).map((s) => s.uci),
              headers: { Event: ["Blundermine", rep.name, name].join(" — "), Site: location.href },
            })}
          />
        </>
      }
      sheet={
        <>
          <LessonModes data={data} n={n} mode={mode} />
          {ended ? <LessonDone data={data} n={n} mode={mode} moves={steps.filter((s) => s.yours).length} missed={0} onAgain={onAgain} /> : null}
          {next ? (
            <div className="drill-note lesson-note">
              {at === 0 && lead ? <Theirs lessons={lessons} lesson={lesson} step={lead} /> : null}
              <div className={at === 0 && lead ? "drill-note-next" : undefined}>
                {next.yours ? (
                  <>
                    <div className="drill-note-title">
                      {next.moment ? <span className="badge lesson-moment">{momentName(next.moment)}</span> : null} Your line plays <b>{said(next)}</b>.
                    </div>
                    {why ? <p>{why}</p> : null}
                    {after ? <p className="faint">{after}</p> : null}
                  </>
                ) : (
                  <Theirs lessons={lessons} lesson={lesson} step={next} next />
                )}
              </div>
            </div>
          ) : null}
          <LessonHead data={data} n={n} />
          <p className="puzzle-evals drill-path">{pathText([...(lesson.kind === "opening" ? [] : lessons.way_in), ...line.lead, ...steps.slice(0, w.at)])}</p>
        </>
      }
    />
  );
}

/** Said in the sheet: your last move and why, their answer, and what is asked now. */
interface Said {
  yours: ReactNode;
  theirs: ReactNode;
  ask: ReactNode;
}

/**
 * Asked: every move of yours from memory — a moment with its badge and
 * what to look for — and a mistake's punishment, watched or tried.
 */
function LessonView({ data, n, mode, back, onAgain, onDone }: ViewProps) {
  const rep = data.repertoire;
  const lessons = data.lessons!;
  const list = lessons.list;
  const lesson = list[n]!;
  const line = boardLine(lesson)!;
  const steps = line.steps;
  const name = titleOf(lesson, rep.name);
  const [said_, setSaid] = useState<Said>({ yours: null, theirs: null, ask: null });
  const [rowText, setRowText] = useState("");
  const [hint, setHint] = useState<{ index: number; level: "" | "square" | "arrow" }>({ index: -1, level: "" });
  const [done, setDone] = useState<{ moves: number; missed: number } | null>(null);
  const track = useRef({ at: 0, missed: new Set<number>(), wrong: new Map<number, number>(), moves: 0 });

  const [puzzle, snapshot] = usePuzzle(
    { fen: line.fen, moves: line.moves },
    {
      onMove: (msg: MoveInfo) => onMove(msg),
      onSolved: () => {
        const missed = track.current.missed.size;
        setDone({ moves: track.current.moves, missed });
        setSaid((s) => ({ ...s, ask: null }));
        const stage = mode === "watch" ? "watched" : "tried";
        onDone(stage);
        saveStage(rep, lesson, stage);
      },
      onFault: (f) => reportFault(f.kind, f.detail, { source: "board", fen: f.fen, moves: f.moves, board: `Lesson: ${rep.name} — ${name}` }),
      askScore: scoreOf,
    }
  );

  const why = (step: LessonStep) => whyOf(lessons, lesson, step);

  /** What is said before your move at `index`, and the arrow when it is shown. */
  function ask(index: number): ReactNode {
    const step = steps[index];
    if (!step || !step.yours) {
      setRowText("");
      return null;
    }
    setRowText(step.moment ? `${momentName(step.moment)}: what do you play?` : "Your move: what does your line play?");
    return (
      <div className="drill-note-title">
        {step.moment ? <span className="badge lesson-moment">{momentName(step.moment)}</span> : null} {mode === "try" && !step.moment ? "Your move." : askText(step.moment, lesson)}
      </div>
    );
  }

  const theirs = (step: LessonStep | undefined): ReactNode => (step ? <Theirs lessons={lessons} lesson={lesson} step={step} /> : null);

  function onMove(msg: MoveInfo) {
    const t = track.current;
    const index = Number(msg.index);
    const step = steps[index];
    setHint({ index: -1, level: "" });
    if (msg.by === "opponent") {
      t.at = index + 1;
      setSaid((s) => ({ ...s, theirs: theirs(step), ask: ask(index + 1) }));
      return;
    }
    if (!step) return;
    if (msg.correct) {
      t.at = index + 1;
      t.moves += 1;
      setRowText("");
      const after = step.moment ? afterText(step.moment) : "";
      setSaid({
        yours: (
          <>
            <div className="drill-note-title good">
              <Icon name="check" /> {said(step)}
            </div>
            {why(step) ? <p>{why(step)}</p> : null}
            {after ? <p className="faint">{after}</p> : null}
          </>
        ),
        theirs: null,
        ask: null,
      });
      return;
    }
    const count = (t.wrong.get(index) || 0) + 1;
    t.wrong.set(index, count);
    t.missed.add(index);
    if (count >= 2) setTimeout(() => reveal(index, "arrow"), 0);
    else
      setSaid((s) => ({
        ...s,
        ask: (
          <>
            {ask(index)}
            <p className="bad">{msg.san} is not the line. Try again.</p>
          </>
        ),
      }));
  }

  // The piece, then the move, then nothing: asking counts as a miss.
  function reveal(index: number, level?: "square" | "arrow") {
    const step = steps[index];
    if (!step || !step.yours) return;
    const last = hint.index === index ? hint.level : "";
    const which = level || (last === "" ? "square" : last === "square" ? "arrow" : "clear");
    if (which === "clear") {
      setHint({ index, level: "" });
      puzzle.hint("clear");
      return;
    }
    setHint({ index, level: which });
    track.current.missed.add(index);
    puzzle.hint(which, [step.uci]);
    setSaid((s) => ({
      ...s,
      ask:
        which === "square" ? (
          <div className="drill-note-title">The marked piece moves.</div>
        ) : (
          <div className="drill-note-title">
            The line plays <b>{said(step)}</b>.
          </div>
        ),
    }));
  }

  useEffect(() => {
    setSaid({ yours: null, theirs: line.lead.length ? theirs(line.lead[line.lead.length - 1]) : null, ask: ask(0) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The row under the board says what is asked, where a phone shows it
  // without scrolling; the sheet says why. The puzzle's own word stands
  // for a wrong move, and once the line is over the row is its stepper.
  const row =
    !done && snapshot.phase === "solving" && snapshot.status.tone !== "error" && rowText ? (
      <span className="board-status tone-neutral" role="status">
        {rowText}
      </span>
    ) : null;
  const hintLabel = hint.level === "square" ? "Show" : hint.level === "arrow" ? "Hide" : "Hint";
  const lead = lesson.kind === "opening" ? [] : lessons.way_in;

  return (
    <BoardScreen
      className={"puzzle-screen drill-line lesson-line" + (done ? " solved" : "")}
      board={<PuzzleBoard puzzle={puzzle} snapshot={snapshot} boardStyle={prefs.board} pieces={prefs.pieces} />}
      line={row || <LineRow puzzle={puzzle} snapshot={snapshot} />}
      bar={
        <>
          <button type="button" className="btn menu-btn" onClick={() => goBack(back)}>
            <Icon name="back" />
            Back
          </button>
          <button
            type="button"
            className={"btn hint-btn" + (done ? " spent" : "")}
            title="Which piece moves; press again for the move itself"
            disabled={Boolean(done)}
            onClick={() => {
              const at = hint.index >= 0 ? hint.index : steps.findIndex((s, i) => i >= track.current.at && s.yours);
              reveal(at);
            }}
          >
            <Icon name="eye" />
            {hintLabel}
          </button>
          <Advance data={data} n={n} mode={mode} done={Boolean(done)} back={back} />
          <BoardMenu
            shown={() => ({
              ...puzzle.shown(),
              headers: { Event: ["Blundermine", rep.name, name].join(" — "), Site: location.href },
            })}
          />
        </>
      }
      sheet={
        <>
          <LessonModes data={data} n={n} mode={mode} />
          {done ? <LessonDone data={data} n={n} mode={mode} moves={done.moves} missed={done.missed} onAgain={onAgain} /> : null}
          <div className="drill-note lesson-note">
            {said_.yours}
            {said_.theirs ? <div className={said_.yours ? "drill-note-next" : undefined}>{said_.theirs}</div> : null}
            {said_.ask ? <div className={said_.yours || said_.theirs ? "drill-note-next" : undefined}>{said_.ask}</div> : null}
          </div>
          <LessonHead data={data} n={n} />
          <LineMoves puzzle={puzzle} snapshot={snapshot} />
          {lead.length ? <p className="puzzle-evals drill-path">{pathText([...lead, ...line.lead])}</p> : null}
        </>
      }
    />
  );
}
